import { InspectionTemplateColumn } from "@/shared/models/Inspection";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Button, Grid2, Typography } from "@mui/material";
import { useGroupDragReorder } from "../hooks/useGroupDragReorder";
import {
	buildGroups,
	groupNamesOf,
	settleGroupNames,
	splitGroups,
} from "../utils/columnGroups";
import { TemplateColumnRow } from "./TemplateColumnRow";

const columnKeys = new WeakMap<InspectionTemplateColumn, number>();
let lastColumnKey = 0;

const keyOf = (column: InspectionTemplateColumn) => {
	const known = columnKeys.get(column);
	if (known !== undefined) return known;

	lastColumnKey += 1;
	columnKeys.set(column, lastColumnKey);
	return lastColumnKey;
};

type Props = {
	columns: InspectionTemplateColumn[];
	disabled?: boolean;
	onChange: (columns: InspectionTemplateColumn[]) => void;
};

export const TemplateColumnsEditor = ({
	columns,
	disabled = false,
	onChange,
}: Props) => {
	const groups = buildGroups(columns);
	const split = splitGroups(columns);

	const { draggedGroup, handleProps, dropTargetProps } = useGroupDragReorder({
		groups,
		disabled,
		onChange,
	});

	const withChange = (
		index: number,
		changes: Partial<InspectionTemplateColumn>,
	) =>
		columns.map((column, position) => {
			if (position !== index) return column;

			const changed = { ...column, ...changes };
			columnKeys.set(changed, keyOf(column));
			return changed;
		});

	const updateColumn = (
		index: number,
		changes: Partial<InspectionTemplateColumn>,
	) => onChange(withChange(index, changes));

	const pickGroup = (index: number, groupName: string | null) =>
		onChange(settleGroupNames(withChange(index, { GroupName: groupName })));

	return (
		<Grid2 container spacing={1}>
			<Grid2 size={{ xs: 12 }}>
				<Typography variant="subtitle2" fontWeight={600}>
					Columns
				</Typography>
				{split.length > 0 && (
					<Typography variant="caption" color="warning.main" display="block">
						{`${split.join(", ")} ${
							split.length === 1 ? "is" : "are"
						} split across the sheet; the next change here puts each group back together.`}
					</Typography>
				)}
			</Grid2>

			<Grid2 size={{ xs: 12 }}>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					{groups.flatMap((group, groupIndex) => {
						const isDragged = draggedGroup === group;
						const rule = isDragged
							? "primary.main"
							: group.columns.length > 1
								? "divider"
								: "transparent";

						return group.columns.map((column, position) => {
							const index = columns.indexOf(column);
							const isFirst = position === 0;
							const isLast = position === group.columns.length - 1;

							return (
								<Box
									key={keyOf(column)}
									data-test-id={isFirst ? "column-group" : undefined}
									data-group-index={groupIndex}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										borderLeft: 2,
										borderColor: rule,
										borderTopLeftRadius: isFirst ? 4 : 0,
										borderBottomLeftRadius: isLast ? 4 : 0,
										pl: 1,
										pt: isFirst ? 0.5 : 0,
										pb: 0.5,
										mb: isLast ? 1 : 0,
										opacity: isDragged ? 0.4 : 1,
										backgroundColor: isDragged ? "action.hover" : "transparent",
									}}
									{...dropTargetProps(group)}
								>
									<Box sx={{ display: "flex", width: 20, flexShrink: 0 }}>
										{isFirst && (
											<Box
												{...handleProps(group)}
												data-test-id="column-drag-handle"
												title={
													group.columns.length > 1
														? `Drag to reorder ${group.columns[0].GroupName} and its ${group.columns.length} columns`
														: "Drag to reorder"
												}
												sx={{
													display: "flex",
													color: "text.disabled",
													cursor: disabled ? "default" : "grab",
													"&:active": {
														cursor: disabled ? "default" : "grabbing",
													},
												}}
											>
												<DragIndicatorIcon fontSize="small" />
											</Box>
										)}
									</Box>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<TemplateColumnRow
											column={column}
											groupNames={groupNamesOf(columns, index)}
											disabled={disabled}
											onChange={(changes) => updateColumn(index, changes)}
											onGroupPicked={(groupName) => pickGroup(index, groupName)}
											onGroupSettled={() => onChange(settleGroupNames(columns))}
											onRemove={() =>
												onChange(
													columns.filter(
														(_column, position) => position !== index,
													),
												)
											}
										/>
									</Box>
								</Box>
							);
						});
					})}
				</Box>
			</Grid2>

			<Grid2 size={{ xs: 12 }}>
				<Button
					startIcon={<AddIcon />}
					data-test-id="add-column-button"
					disabled={disabled}
					onClick={() =>
						onChange([
							...columns,
							{
								ColumnId: null,
								TemplateId: null,
								GroupName: null,
								ColumnLabel: "",
								ValueTypeCode: "G",
								IsActive: true,
								HasReadings: false,
								SortOrder: columns.length + 1,
							},
						])
					}
				>
					Add Column
				</Button>
			</Grid2>
		</Grid2>
	);
};
